import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactSlider from 'react-slider';
import api from '../api/axios';
import './AddProductsToCampaignModal.css';
import '../pages/QuoteCartPage.css';

const PAGE_SIZE = 20;

const fmt = (n) =>
    n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : null;

const brandOf = (p) => p.brandName || (typeof p.brand === 'string' ? p.brand : p.brand?.name) || '';

// Details for the products already in the campaign. /products/by-ids skips products removed from
// the shop, so any it doesn't return are fetched one by one — a removed product still assigned to
// the campaign must show up here, or it could never be unticked.
async function loadDetails(ids) {
    const found = new Map();
    for (let i = 0; i < ids.length; i += 100) {
        const chunk = ids.slice(i, i + 100);
        const res = await api.get('/products/by-ids', { params: { ids: chunk.join(',') } }).catch(() => ({ data: [] }));
        (res.data || []).forEach(p => found.set(p.id, p));
    }
    const missing = ids.filter(id => !found.has(id));
    const singles = await Promise.all(missing.map(id => api.get(`/products/${id}`).then(r => r.data).catch(() => null)));
    singles.forEach((p, i) => found.set(missing[i], p || { id: missing[i], name: `Product #${missing[i]}`, missingProduct: true }));
    return found;
}

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

const AddProductsToCampaignModal = ({ campaign, onClose, onSuccess, initialSelectedIds, onSave }) => {
    // ── Filter state ─────────────────────────────────────────────
    const [categories, setCategories]     = useState([]);
    const [brands, setBrands]             = useState([]);
    const [priceBounds, setPriceBounds]   = useState([0, 100000]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedBrand, setSelectedBrand]       = useState('');
    const [priceRange, setPriceRange]             = useState([0, 100000]);

    // ── Product / search state ───────────────────────────────────
    const [products, setProducts]         = useState([]);
    const [searchTerm, setSearchTerm]     = useState('');
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingMore, setLoadingMore]   = useState(false);
    const [currentPage, setCurrentPage]   = useState(0);
    const [hasMore, setHasMore]           = useState(false);

    // ── Selection state ──────────────────────────────────────────
    const [selectedProductIds, setSelectedProductIds] = useState(
        initialSelectedIds instanceof Set ? initialSelectedIds : new Set()
    );
    const [selectAll, setSelectAll]       = useState(false);

    // ── "In this campaign" view ──────────────────────────────────
    // Opens on what's already assigned (when there is anything), so removing products doesn't mean
    // hunting for them in the whole catalogue. The list is everything assigned when the modal
    // opened plus anything ticked since; unticked rows stay (marked "will be removed") until saved,
    // so a mis-click can be undone.
    const initialIds = useRef(initialSelectedIds instanceof Set ? new Set(initialSelectedIds) : new Set());
    const [view, setView] = useState(initialIds.current.size > 0 ? 'assigned' : 'catalog');
    const [listedIds, setListedIds] = useState(() => [...initialIds.current]);
    const [details, setDetails] = useState(() => new Map());
    const [loadingDetails, setLoadingDetails] = useState(false);

    const [error, setError]   = useState('');
    const [saving, setSaving] = useState(false);

    const activeQueryRef = useRef('');

    const debouncedSearch   = useDebounce(searchTerm, 400);
    const debouncedPrice    = useDebounce(priceRange, 600);

    // ── Load filters on mount ────────────────────────────────────
    useEffect(() => {
        Promise.all([
            api.get('/categories'),
            api.get('/brands'),
            api.get('/products/price-range'),
        ]).then(([catRes, brandRes, rangeRes]) => {
            setCategories(catRes.data || []);
            setBrands(brandRes.data || []);
            if (rangeRes.data) {
                const bounds = [Math.floor(rangeRes.data.minPrice), Math.ceil(rangeRes.data.maxPrice)];
                setPriceBounds(bounds);
                setPriceRange(bounds);
            }
        }).catch(() => {});
    }, []);

    // ── Pre-selection from campaign data ─────────────────────────
    useEffect(() => {
        if (!(initialSelectedIds instanceof Set) && campaign?.campaignProducts) {
            const ids = new Set(
                campaign.campaignProducts.map(cp => cp?.id?.productId).filter(Boolean)
            );
            setSelectedProductIds(ids);
        }
    }, [campaign, initialSelectedIds]);

    // ── Fetch products (paginated) ───────────────────────────────
    const fetchProducts = useCallback(async (category, brand, search, minPrice, maxPrice, page) => {
        const queryKey = `${category}|${brand}|${search}|${minPrice}|${maxPrice}|${page}`;
        activeQueryRef.current = queryKey;

        const isNew = page === 0;
        isNew ? setLoadingProducts(true) : setLoadingMore(true);
        setError('');

        try {
            const params = { page, size: PAGE_SIZE };
            if (category) params.category = category;
            if (brand)    params.brand    = brand;
            if (search)   params.search   = search;
            if (minPrice != null) params.minPrice = minPrice;
            if (maxPrice != null) params.maxPrice = maxPrice;

            const res = await api.get('/products', { params });
            if (activeQueryRef.current !== queryKey) return;

            const items = res.data?.content || [];
            setProducts(prev => isNew ? items : [...prev, ...items]);
            setHasMore(!res.data?.last);
            setCurrentPage(page);
        } catch {
            if (activeQueryRef.current === queryKey && page === 0) setProducts([]);
        } finally {
            isNew ? setLoadingProducts(false) : setLoadingMore(false);
        }
    }, []);

    // Re-fetch when filters or debounced search changes
    useEffect(() => {
        fetchProducts(selectedCategory, selectedBrand, debouncedSearch, debouncedPrice[0], debouncedPrice[1], 0);
    }, [selectedCategory, selectedBrand, debouncedSearch, debouncedPrice, fetchProducts]);

    // Details for the listed products, fetched the first time the assigned view needs them.
    useEffect(() => {
        if (view !== 'assigned') return undefined;
        const need = listedIds.filter(id => !details.has(id));
        if (!need.length) return undefined;
        let alive = true;
        setLoadingDetails(true);
        loadDetails(need)
            .then(found => alive && setDetails(prev => { const next = new Map(prev); found.forEach((v, k) => next.set(k, v)); return next; }))
            .finally(() => alive && setLoadingDetails(false));
        return () => { alive = false; };
    }, [view, listedIds, details]);

    // The assigned list, narrowed by the same search and filters as the catalogue (applied here in
    // the browser, since these products are already loaded), A–Z.
    const assignedShown = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        const [min, max] = debouncedPrice;
        const fullRange = min <= priceBounds[0] && max >= priceBounds[1];
        return listedIds
            .map(id => details.get(id))
            .filter(Boolean)
            .filter(p => !q || (p.name || '').toLowerCase().includes(q) || String(p.id) === q)
            .filter(p => !selectedCategory || p.category === selectedCategory)
            .filter(p => !selectedBrand || brandOf(p) === selectedBrand)
            .filter(p => {
                if (fullRange) return true;
                const price = p.marketOperatingPrice || p.mrp;
                return price == null || (price >= min && price <= max);
            })
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [listedIds, details, debouncedSearch, debouncedPrice, priceBounds, selectedCategory, selectedBrand]);

    const toRemove = [...initialIds.current].filter(id => !selectedProductIds.has(id)).length;
    const toAdd = [...selectedProductIds].filter(id => !initialIds.current.has(id)).length;
    const shownSelected = assignedShown.filter(p => selectedProductIds.has(p.id)).length;

    // Untick everything the current filters show, e.g. filter to a brand, then remove it all.
    const removeShown = () => {
        const shown = new Set(assignedShown.map(p => p.id));
        setSelectedProductIds(prev => new Set([...prev].filter(id => !shown.has(id))));
    };

    // ── Handlers ─────────────────────────────────────────────────
    const toggleProduct = (id, product) => {
        if (selectAll) return;
        if (product) setDetails(prev => (prev.has(id) ? prev : new Map(prev).set(id, product)));
        setListedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSelectAllChange = (e) => {
        setSelectAll(e.target.checked);
        if (e.target.checked) setSelectedProductIds(new Set());
    };

    const clearFilters = () => {
        setSelectedCategory('');
        setSelectedBrand('');
        setPriceRange(priceBounds);
        setSearchTerm('');
    };

    const handleSubmit = async () => {
        // onSave is required — the caller owns the endpoint, since campaign
        // products are written to a different API per surface (Brandstore uses
        // PUT /admin/brandstore/campaigns/:id/products). A missing prop is a
        // programming error, so fail loudly here rather than letting the catch
        // below report it to the user as a failed save.
        if (typeof onSave !== 'function') {
            throw new Error(
                'AddProductsToCampaignModal: the onSave prop is required and must be a function.'
            );
        }

        setSaving(true);
        setError('');
        const payload = {
            selectAll,
            productIds: selectAll ? [] : Array.from(selectedProductIds),
        };
        try {
            await onSave(payload);
            onSuccess();
        } catch (err) {
            console.error('Failed to save campaign products:', err);
            setError('Failed to save products. Please try again.');
            setSaving(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────
    return (
        <div className="modal-backdrop">
            <div className="modal-content product-selection-modal">

                {/* Header */}
                <div className="modal-header">
                    <h2>Products in "{campaign.name}"</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>

                {/* Body */}
                <div className="modal-body-with-sidebar" style={{ overflow: 'hidden' }}>

                    {/* ── Left: Filters ── */}
                    <aside className="apcm-filters">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                            <span className="qcp-panel__title" style={{ fontSize: '1rem', marginBottom: 0 }}>FILTERS</span>
                            <button className="qcp-reset-btn" style={{ width: 'auto', marginTop: 0, padding: '4px 12px' }} onClick={clearFilters}>
                                Clear All
                            </button>
                        </div>

                        <div className="qcp-filter-section">
                            <span className="qcp-filter-label">Category</span>
                            <select
                                className="qcp-filter-select"
                                value={selectedCategory}
                                onChange={e => setSelectedCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>

                        <div className="qcp-filter-section">
                            <span className="qcp-filter-label">Brand</span>
                            <select
                                className="qcp-filter-select"
                                value={selectedBrand}
                                onChange={e => setSelectedBrand(e.target.value)}
                            >
                                <option value="">All Brands</option>
                                {brands.map(b => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
                            </select>
                        </div>

                        <div className="qcp-filter-section">
                            <span className="qcp-filter-label">Price Range</span>
                            <div className="qcp-price-info">
                                <span>{fmt(priceRange[0])}</span>
                                <span>{fmt(priceRange[1])}</span>
                            </div>
                            <ReactSlider
                                className="qcp-price-slider"
                                thumbClassName="qcp-thumb"
                                trackClassName="qcp-track"
                                value={priceRange}
                                min={priceBounds[0]}
                                max={priceBounds[1]}
                                onChange={setPriceRange}
                                pearling
                                minDistance={100}
                            />
                        </div>
                    </aside>

                    {/* ── Right: what's in the campaign, or the catalogue to add from ── */}
                    <main className="apcm-catalog">
                        <div className="apcm-tabs" role="tablist" aria-label="Products">
                            <button type="button" role="tab" aria-selected={view === 'assigned'}
                                className={`apcm-tab${view === 'assigned' ? ' is-on' : ''}`} onClick={() => setView('assigned')}>
                                In this campaign <span className="apcm-tab-count">{selectAll ? 'All' : selectedProductIds.size}</span>
                            </button>
                            <button type="button" role="tab" aria-selected={view === 'catalog'}
                                className={`apcm-tab${view === 'catalog' ? ' is-on' : ''}`} onClick={() => setView('catalog')}>
                                Add from catalog
                            </button>
                        </div>

                        {error && <p className="error-message">{error}</p>}

                        {/* Search input */}
                        <div className="qcp-search-wrap">
                            <svg className="qcp-search-ico" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                            <input
                                className="qcp-search-input"
                                type="text"
                                placeholder={view === 'assigned' ? 'Search this campaign’s products…' : 'Search the catalog…'}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            {(view === 'assigned' ? loadingDetails : loadingProducts) && <div className="qcp-search-spinner" />}
                        </div>

                        {view === 'assigned' && !selectAll && assignedShown.length > 0 && (
                            <div className="apcm-assigned-bar">
                                <span>
                                    Showing {assignedShown.length} of {listedIds.length}
                                    {toRemove > 0 && <> · <strong className="apcm-remove-count">{toRemove} to remove</strong></>}
                                    {toAdd > 0 && <> · <strong>{toAdd} to add</strong></>}
                                </span>
                                {shownSelected > 0 && (
                                    <button type="button" className="qcp-reset-btn" style={{ width: 'auto', marginTop: 0, padding: '4px 12px' }} onClick={removeShown}>
                                        {assignedShown.length < listedIds.length ? `Remove ${shownSelected} shown` : 'Remove all'}
                                    </button>
                                )}
                            </div>
                        )}

                        {view === 'assigned' ? (
                            <div className="apcm-results">
                                {selectAll ? (
                                    <div className="apcm-empty">Every product is selected (“Select all” below). Untick it to pick products one by one.</div>
                                ) : listedIds.length === 0 ? (
                                    <div className="apcm-empty">
                                        No products in this campaign yet.
                                        <button type="button" className="qcp-reset-btn" style={{ width: 'auto', margin: '12px auto 0', padding: '7px 20px', display: 'block' }} onClick={() => setView('catalog')}>
                                            Add from catalog
                                        </button>
                                    </div>
                                ) : loadingDetails && assignedShown.length === 0 ? (
                                    <div className="apcm-empty">Loading…</div>
                                ) : assignedShown.length === 0 ? (
                                    <div className="apcm-empty">None of this campaign’s products match the search or filters.</div>
                                ) : assignedShown.map(product => {
                                    const checked = selectedProductIds.has(product.id);
                                    const price = product.marketOperatingPrice || product.mrp;
                                    const leaving = !checked && initialIds.current.has(product.id);
                                    return (
                                        <div
                                            key={product.id}
                                            className={`qcp-product-row${leaving ? ' apcm-row--leaving' : ''}`}
                                            style={{ cursor: 'pointer', background: checked ? 'var(--color-primary-subtle, #FFF8EC)' : undefined }}
                                            onClick={() => toggleProduct(product.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {}}
                                                aria-label={`${checked ? 'Remove' : 'Keep'} ${product.name}`}
                                                style={{ flexShrink: 0, width: 16, height: 16, accentColor: 'var(--color-primary, #F29213)', cursor: 'pointer' }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <div className="qcp-product-row__img">
                                                {product.thumbnailUrl ? <img src={product.thumbnailUrl} alt="" /> : <span>📦</span>}
                                            </div>
                                            <div className="qcp-product-row__info">
                                                <div className="qcp-product-row__name">{product.name}</div>
                                                <div className="qcp-product-row__meta">
                                                    {brandOf(product) && <span className="qcp-product-row__cat">{brandOf(product)}</span>}
                                                    {price && <span className="qcp-product-row__price">{fmt(price)}</span>}
                                                    {product.approvalStatus === 'REMOVED' && <span className="apcm-flag">Removed from shop</span>}
                                                    {product.missingProduct && <span className="apcm-flag">No longer exists</span>}
                                                    {leaving && <span className="apcm-flag apcm-flag--leaving">Will be removed</span>}
                                                    {checked && !initialIds.current.has(product.id) && <span className="apcm-flag apcm-flag--new">New</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                        <div className="apcm-results">
                            {!loadingProducts && products.length === 0 ? (
                                <div style={{ padding: '40px 0', textAlign: 'center', color: '#9CA3AF' }}>No products found.</div>
                            ) : (
                                products.map(product => {
                                    const checked = selectAll || selectedProductIds.has(product.id);
                                    const price = product.marketOperatingPrice || product.mrp;
                                    return (
                                        <div
                                            key={product.id}
                                            className="qcp-product-row"
                                            style={{ cursor: 'pointer', background: checked ? 'var(--color-primary-subtle, #FFF8EC)' : undefined }}
                                            onClick={() => toggleProduct(product.id, product)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {}}
                                                disabled={selectAll}
                                                style={{ flexShrink: 0, width: 16, height: 16, accentColor: 'var(--color-primary, #F29213)', cursor: 'pointer' }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <div className="qcp-product-row__img">
                                                {product.thumbnailUrl
                                                    ? <img src={product.thumbnailUrl} alt={product.name} />
                                                    : <span>📦</span>}
                                            </div>
                                            <div className="qcp-product-row__info">
                                                <div className="qcp-product-row__name">{product.name}</div>
                                                <div className="qcp-product-row__meta">
                                                    {brandOf(product) && <span className="qcp-product-row__cat">{brandOf(product)}</span>}
                                                    {initialIds.current.has(product.id) && <span className="apcm-flag apcm-flag--in">In campaign</span>}
                                                    {price && <span className="qcp-product-row__price">{fmt(price)}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}

                            {hasMore && !loadingProducts && (
                                <div style={{ textAlign: 'center', padding: '14px 0' }}>
                                    <button
                                        type="button"
                                        onClick={() => fetchProducts(selectedCategory, selectedBrand, debouncedSearch, debouncedPrice[0], debouncedPrice[1], currentPage + 1)}
                                        disabled={loadingMore}
                                        className="qcp-reset-btn"
                                        style={{ width: 'auto', padding: '7px 24px', marginTop: 0 }}
                                    >
                                        {loadingMore ? 'Loading…' : 'Load more'}
                                    </button>
                                </div>
                            )}
                        </div>
                        )}
                    </main>
                </div>

                {/* Footer */}
                <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAllChange} style={{ width: 15, height: 15, accentColor: 'var(--color-primary, #F29213)' }} />
                        <span style={{ color: '#6B7280' }}>
                            {selectAll ? 'All products selected' : `${selectedProductIds.size} product${selectedProductIds.size !== 1 ? 's' : ''} selected`}
                            {!selectAll && (toAdd > 0 || toRemove > 0) && ` (${[toAdd ? `+${toAdd} new` : null, toRemove ? `${toRemove} to remove` : null].filter(Boolean).join(', ')})`}
                        </span>
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                        <button type="button" onClick={handleSubmit} disabled={saving} className="btn-primary">
                            {saving ? 'Saving…' : 'Save Products'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProductsToCampaignModal;
